import type {ValidationResult} from '../../../utils/domain-validation';

export type {ValidationResult};

export function validateMetadataUrl(url: string): ValidationResult {
    const trimmed = url.trim();
    if (trimmed.length === 0) {
        return {valid: false, error: 'URL is required.'};
    }
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
        return {valid: false, error: 'URL must start with http:// or https://.'};
    }
    try {
        // eslint-disable-next-line no-new
        new URL(trimmed);
    } catch {
        return {valid: false, error: 'Invalid URL format.'};
    }
    return {valid: true, error: null};
}

export function validateLoginUrl(url: string): ValidationResult {
    const trimmed = url.trim();
    if (trimmed.length === 0) {
        return {valid: false, error: 'URL is required.'};
    }
    if (!trimmed.startsWith('https://')) {
        return {valid: false, error: 'SSO login URL must use HTTPS.'};
    }
    try {
        const parsed = new URL(trimmed);
        if (parsed.pathname === '/' || parsed.pathname === '') {
            return {valid: false, error: 'URL must include a path (e.g. /sso/saml).'};
        }
        const path = parsed.pathname.toLowerCase();
        if (!path.includes('saml') && !path.includes('sso')) {
            return {valid: true, error: null, warning: 'This URL doesn\'t appear to be a SAML SSO endpoint. Make sure it points to your IdP\'s SSO login URL.'};
        }
    } catch {
        return {valid: false, error: 'Invalid URL format.'};
    }
    return {valid: true, error: null};
}

export function validatePemCertificate(cert: string): ValidationResult {
    const trimmed = cert.trim();
    if (trimmed.length === 0) {
        return {valid: false, error: 'Certificate is required.'};
    }
    if (trimmed.includes('-----BEGIN CERTIFICATE-----') && !trimmed.includes('-----END CERTIFICATE-----')) {
        return {valid: false, error: 'Certificate must be PEM-encoded (ends with -----END CERTIFICATE-----).'};
    }
    return {valid: true, error: null};
}

export function validateXmlFile(file: File): Promise<ValidationResult> {
    return new Promise(resolve => {
        const name = file.name.toLowerCase();
        if (!name.endsWith('.xml')) {
            resolve({valid: false, error: 'File must be an XML file (.xml).'});
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result as string || '';
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/xml');

            const parseError = doc.querySelector('parsererror');
            if (parseError) {
                resolve({valid: false, error: 'File is not valid XML. Please check the file and try again.'});
                return;
            }

            const root = doc.documentElement;
            const localName = root.localName;
            if (localName !== 'EntityDescriptor' && localName !== 'EntitiesDescriptor') {
                resolve({valid: false, error: 'File does not appear to be SAML metadata. Expected an EntityDescriptor or EntitiesDescriptor root element.'});
                return;
            }

            resolve({valid: true, error: null});
        };
        reader.onerror = () => {
            resolve({valid: false, error: 'Failed to read the file. Please try again.'});
        };
        reader.readAsText(file);
    });
}
